// PatchNotesAdminPage.tsx - Admin interface for managing patch notes
import React, { useState, useEffect } from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface PatchNote {
  id: string;
  version_major: number;
  version_minor: number;
  version_patch: number;
  change_type: 'bugfix' | 'feature' | 'major';
  title: string;
  body: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

const CHANGE_TYPE_LABELS: Record<string, { label: string; color: string; description: string }> = {
  bugfix:  { label: 'Bug Fix',       color: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',  description: 'Increments patch (x.x.1 → x.x.2)' },
  feature: { label: 'New Feature',   color: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',     description: 'Increments minor (x.1.x → x.2.0)' },
  major:   { label: 'Major Release', color: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30', description: 'Increments major (0.x.x → 1.0.0)' },
};

function formatVersion(pn: PatchNote): string {
  return `${pn.version_major}.${pn.version_minor}.${pn.version_patch}`;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function wasEdited(pn: PatchNote): boolean {
  // Consider "edited" if updated_at is more than 5 seconds after created_at
  const created = new Date(pn.created_at).getTime();
  const updated = new Date(pn.updated_at).getTime();
  return (updated - created) > 5000;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PatchNotesAdminPage() {
  const [patchNotes, setPatchNotes] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formChangeType, setFormChangeType] = useState<'bugfix' | 'feature' | 'major'>('feature');

  // Filter state
  const [filterStatus, setFilterStatus] = useState<'active' | 'deleted' | 'all'>('active');

  // ---- FETCH DATA ----
  useEffect(() => { fetchPatchNotes(); }, []);

  async function fetchPatchNotes() {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/patch-notes', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      setPatchNotes(await res.json());
      setError(null);
    } catch (e) {
      setError('Failed to load patch notes');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  // ---- MODAL HANDLERS ----
  function openCreateModal() {
    setEditingId(null);
    setFormTitle('');
    setFormBody('');
    setFormChangeType('feature');
    setModalOpen(true);
  }

  function openEditModal(pn: PatchNote) {
    setEditingId(pn.id);
    setFormTitle(pn.title);
    setFormBody(pn.body || '');
    setModalOpen(true);
  }

  async function handleSave() {
    if (!formTitle.trim()) {
      alert('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { title: formTitle.trim(), body: formBody.trim() || null, change_type: formChangeType };
      const url = editingId
        ? `/api/admin/patch-notes/${editingId}`
        : '/api/admin/patch-notes';
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }
      setModalOpen(false);
      fetchPatchNotes();
    } catch (e: any) {
      alert(e.message || 'Failed to save patch note');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this patch note?')) return;
    try {
      const res = await fetch(`/api/admin/patch-notes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete');
      fetchPatchNotes();
    } catch (e) {
      alert('Failed to delete patch note');
    }
  }

  async function handleRestore(id: string) {
    try {
      const res = await fetch(`/api/admin/patch-notes/${id}/restore`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to restore');
      fetchPatchNotes();
    } catch (e) {
      alert('Failed to restore patch note');
    }
  }

  // ---- FILTER ----
  const filtered = patchNotes.filter(pn => {
    if (filterStatus === 'active') return pn.is_active;
    if (filterStatus === 'deleted') return !pn.is_active;
    return true;
  });

  // ---- RENDER ----
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--c-ink)]">Patch Notes</h2>
          <p className="text-sm text-[var(--c-ink-3)] mt-1">
            {patchNotes.filter(p => p.is_active).length} published patch notes
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors"
        >
          + New Patch Note
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {(['active', 'deleted', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilterStatus(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize ${
              filterStatus === f
                ? 'bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]'
                : 'bg-transparent border-[var(--c-line-soft)] text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-[var(--c-ink-4)] animate-pulse">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[var(--c-ink-4)]">
            No patch notes found.
          </div>
        ) : (
          filtered.map(pn => (
            <div
              key={pn.id}
              className={`p-4 rounded-xl border transition-colors ${
                pn.is_active
                  ? 'bg-[var(--c-bg-veil)] border-[var(--c-line)] hover:border-[var(--c-line-strong)]'
                  : 'bg-red-500/5 border-red-500/10 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Version badge + type + title */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30">
                      v{formatVersion(pn)}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${CHANGE_TYPE_LABELS[pn.change_type]?.color || ''}`}>
                      {CHANGE_TYPE_LABELS[pn.change_type]?.label || pn.change_type}
                    </span>
                    {!pn.is_active && (
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-red-500/20 text-red-700 dark:text-red-300 border border-red-500/30">
                        Deleted
                      </span>
                    )}
                    <h3 className="text-sm font-bold text-[var(--c-ink)] truncate">{pn.title}</h3>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-[10px] text-[var(--c-ink-4)]">
                    <span>Created {formatDate(pn.created_at)}</span>
                    {pn.created_by_name && <span>by {pn.created_by_name}</span>}
                    {wasEdited(pn) && (
                      <span className="text-amber-700/60 dark:text-amber-400/60">
                        (edited {formatDate(pn.updated_at)})
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openEditModal(pn)}
                    className="px-3 py-1.5 text-xs font-medium text-[var(--c-ink-2)] hover:text-[var(--c-ink)] border border-[var(--c-line)] hover:border-[var(--c-line-strong)] rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  {pn.is_active ? (
                    <button
                      onClick={() => handleDelete(pn.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-500/20 hover:border-red-500/40 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRestore(pn.id)}
                      className="px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 border border-green-500/20 hover:border-green-500/40 rounded-lg transition-colors"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>

              {/* Body Preview */}
              {pn.body && (
                <div className="mt-3 pt-3 border-t border-[var(--c-line-soft)]">
                  <div className="text-[10px] text-[var(--c-ink-4)] uppercase mb-1">Preview</div>
                  <div
                    className="text-xs text-[var(--c-ink-3)] line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: pn.body }}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-2xl bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-5 border-b border-[var(--c-line)] flex justify-between items-center">
              <h2 className="text-lg font-bold text-[var(--c-ink)]">
                {editingId ? 'Edit Patch Note' : 'New Patch Note'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] text-xl">
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {!editingId && (() => {
                const active = patchNotes.filter(p => p.version_major !== undefined);
                const cur = active.length > 0 ? active[0] : null; // Already sorted DESC
                let preview = '0.1.0';
                if (cur) {
                  if (formChangeType === 'bugfix') preview = `${cur.version_major}.${cur.version_minor}.${cur.version_patch + 1}`;
                  else if (formChangeType === 'feature') preview = `${cur.version_major}.${cur.version_minor + 1}.0`;
                  else if (formChangeType === 'major') preview = `${cur.version_major + 1}.0.0`;
                }
                return (
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                    <p className="text-xs text-cyan-700 dark:text-cyan-300">
                      Next version: <span className="font-bold">v{preview}</span>
                    </p>
                  </div>
                );
              })()}

              {/* Change Type */}
              {!editingId && (
                <div>
                  <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Change Type</label>
                  <div className="flex gap-2">
                    {(['bugfix', 'feature', 'major'] as const).map(ct => (
                      <button
                        key={ct}
                        type="button"
                        onClick={() => setFormChangeType(ct)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          formChangeType === ct
                            ? CHANGE_TYPE_LABELS[ct].color
                            : 'bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:text-[var(--c-ink)]'
                        }`}
                      >
                        <div>{CHANGE_TYPE_LABELS[ct].label}</div>
                        <div className="text-[10px] opacity-60 mt-0.5">{CHANGE_TYPE_LABELS[ct].description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Title *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="e.g. Squad System, Soft Reserve Improvements"
                  className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm placeholder:text-[var(--c-ink-4)] focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">
                  Changes (HTML supported)
                </label>
                <textarea
                  value={formBody}
                  onChange={e => setFormBody(e.target.value)}
                  placeholder={"<ul>\n  <li>Added squad formation for friend groups</li>\n  <li>Fixed timezone display in availability</li>\n  <li>Improved mobile layout</li>\n</ul>"}
                  rows={10}
                  className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm placeholder:text-[var(--c-ink-4)] focus:border-cyan-500 focus:outline-none font-mono"
                />
                <p className="text-[10px] text-[var(--c-ink-4)] mt-1">
                  Use &lt;ul&gt;&lt;li&gt; for lists, &lt;strong&gt; for bold, &lt;em&gt; for italic, &lt;a href="..."&gt; for links.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-[var(--c-line)] flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-[var(--c-ink-2)] hover:text-[var(--c-ink)] border border-[var(--c-line)] hover:border-[var(--c-line-strong)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : (editingId ? 'Update' : 'Publish')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
